import { concatStrings, getRecipientColor } from '../../lib/utils';
import type { ISignerDetails } from '../../store/envelopeHistory/types';
import type { IEnvelopeMetadata } from '../../store/envelopeManagement/types';
import { isSenderOwnSignerRow } from '../utils';

/**
 * One shape for both entry points: Envelope Sent reads signerId / signingStatus
 * off `senderBuild`, the dashboard gets id / status over GraphQL.
 */
export interface IInPersonCandidate {
  signerId: string;
  name: string;
  email: string;
  /** True for the sender's own recipient row, which keeps the legacy Sign Now path. */
  isSender: boolean;
  /** Accent color for the picker row, matching the recipient colors used elsewhere. */
  color: string;
  /** Carried rather than split out of `name`, which may be a company or email. */
  firstName: string;
  /** False for a signer in a later group: listed, but not selectable yet. */
  isEligible: boolean;
}

function displayName(firstName?: string, middleName?: string, lastName?: string, email?: string) {
  return concatStrings(firstName, middleName, lastName).trim() || email || 'Unnamed signer';
}

/** Structural so both the GraphQL result and the store's body fit without a cast. */
export interface IRecipientLike {
  id: string;
  role?: string;
  status?: string;
  title?: string;
  recipientColorCounter?: number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
}

export function candidatesFromRecipients(
  recipients: IRecipientLike[],
  senderEmail?: string,
  signerDetails?: ISignerDetails
): IInPersonCandidate[] {
  // A later group still reports InProgress, so status cannot tell us whose turn
  // it is. Without activeGroup, mark everyone eligible and let the service refuse.
  const { activeGroup, details } = signerDetails ?? {};
  function isUp(signerId: string) {
    return !activeGroup || details?.find((d) => d.value.signerId === signerId)?.value.signingGroupId === activeGroup;
  }

  return (recipients || [])
    .filter((r) => r.role === 'Signer' && r.status?.toLowerCase() === 'inprogress')
    .map((r, i) => {
      const isSender = isSenderOwnSignerRow(r, senderEmail);
      const name = displayName(r.firstName, r.middleName, r.lastName, r.email);
      return {
        signerId: r.id,
        name,
        firstName: r.firstName?.trim() || name,
        email: r.email || '',
        isSender,
        isEligible: isUp(r.id),
        // No color on the query; accent by position. Decoration, not identity.
        color: getRecipientColor(isSender ? 0 : i + 1),
      };
    });
}

/**
 * Row-level check for whether to offer "Sign Envelope" at all. The list endpoint
 * returns counts but no recipients, so the picker fetches names when opened.
 */
export function envelopeCanBeSignedInPerson(envelope: IEnvelopeMetadata): boolean {
  const status = envelope.status?.toLowerCase();
  if (status !== 'sent' && status !== 'inprogress') return false;
  return envelope.signerCount > envelope.completedSignerCount;
}

/**
 * signNow renders the signer app's "I'm done" button; inPerson sends it back to
 * DigiSign instead of the envelope's own redirect, which for a Forms envelope
 * would drop a client inside the agent's Forms session. The token is in the
 * fragment, which this leaves alone.
 */
export function withReturnToSender(signingUrl: string): string {
  const url = new URL(signingUrl);
  url.searchParams.set('signNow', 'true');
  url.searchParams.set('inPerson', 'true');
  return url.toString();
}
